// inspiration :
// https://github.com/onderonur/react-infinite-scroll-hook/blob/main/packages/react-infinite-scroll-hook/src/use-infinite-scroll.ts
import { useCallback, useEffect, useRef } from 'react';
import { useTrackVisibility } from 'react-intersection-observer-hook';

const DEFAULT_DELAY_IN_MS = 100;

export function useScrolling(
  { response, scrolling },
  { delay = DEFAULT_DELAY_IN_MS, preloadedPages = 1 } = {},
) {
  const loading = response.pending;
  const sentryIndex =
    response.data.length - scrolling.initialPer * preloadedPages;
  const hasNextPage = !scrolling.isLastPage;
  const onLoadMore = scrolling.nextPage;
  const disabled = Boolean(response.error);

  const savedCallbackRef = useRef(onLoadMore);
  const [ref, { rootRef, isVisible }] = useTrackVisibility();
  // {
  //   rootMargin:0,
  // });

  useEffect(() => {
    savedCallbackRef.current = onLoadMore;
  }, [onLoadMore]);

  const shouldLoadMore = !disabled && !loading && isVisible && hasNextPage;

  useEffect(() => {
    if (shouldLoadMore) {
      // When we trigger 'onLoadMore' and new items are added to the list,
      // right before they become rendered on the screen, 'loading' becomes false
      // and 'isVisible' can be true for a brief time, based on the scroll position.
      // So, it triggers 'onLoadMore' just after the first one is finished.
      // We use a small delay here to prevent this kind of situations.
      // It can be configured by hook args.
      const timer = setTimeout(() => {
        savedCallbackRef.current();
      }, delay);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [shouldLoadMore, delay]);

  const sentryItem = response.data.at(sentryIndex);
  const setSentry = useCallback(
    (item, getId = (x) => x?.id) =>
      getId(item) === getId(sentryItem) ? ref : null,
    [sentryItem, ref],
  );

  return { setSentry, sentry: ref, root: rootRef, hasNextPage };
}
